/**
 * Review Main Section
 * 
 * Handles:
 * - Filter tab switching (show/hide items by tag)
 * - Splide slider for filter pills on mobile
 * - Pagination with "Show More" button
 * - Shopify theme editor support
 */

class ReviewMainSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    
    // Elements
    this.filterSlider = section.querySelector('[data-review-filter-slider]');
    this.filterBar = section.querySelector('[data-review-filter-bar]');
    this.filterPills = section.querySelectorAll('[data-filter]');
    this.grid = section.querySelector('[data-review-grid]');
    this.items = section.querySelectorAll('.review-main__item');
    this.showMoreWrapper = section.querySelector('[data-show-more-wrapper]');
    this.showMoreBtn = section.querySelector('[data-show-more-btn]');
    
    // Splide instance for filter pills
    this.filterSplide = null;
    
    // Pagination state
    this.itemsPerPage = parseInt(this.grid?.dataset.itemsPerPage || 10, 10);
    this.currentPage = 1;
    
    // State
    this.activeFilter = 'all';
    
    // Masonry state
    this.resizeObserver = null;
    this.layoutTimeout = null;
    
    // Bind methods
    this.handleFilterClick = this.handleFilterClick.bind(this);
    this.handleShowMore = this.handleShowMore.bind(this);
    this.layoutMasonry = this.layoutMasonry.bind(this);
    
    this.init();
  }
  
  init() {
    // Initialize filter pills slider for mobile
    this.initFilterSlider();
    
    // Initialize masonry layout
    this.initMasonry();
    
    // Filter pills
    this.filterPills.forEach(pill => {
      pill.addEventListener('click', this.handleFilterClick);
    });
    
    // Show more button
    if (this.showMoreBtn) {
      this.showMoreBtn.addEventListener('click', this.handleShowMore);
    }
    
    // Initial pagination state
    this.updatePagination();
  }
  
  /**
   * Initialize masonry layout
   */
  initMasonry() {
    if (!this.grid) return;
    
    // Initial layout after images load
    this.layoutMasonry();
    
    // Re-layout when images load
    this.grid.querySelectorAll('img').forEach(img => {
      if (!img.complete) {
        img.addEventListener('load', () => this.debouncedLayout());
      }
    });
    
    // Re-layout on resize
    this.resizeObserver = new ResizeObserver(() => this.debouncedLayout());
    this.resizeObserver.observe(this.grid);
  }
  
  /**
   * Debounced layout to prevent excessive reflows
   */
  debouncedLayout() {
    if (this.layoutTimeout) {
      cancelAnimationFrame(this.layoutTimeout);
    }
    this.layoutTimeout = requestAnimationFrame(() => this.layoutMasonry());
  }
  
  /**
   * Calculate and apply masonry positions
   */
  layoutMasonry() {
    if (!this.grid) return;
    
    const gridWidth = this.grid.offsetWidth;
    const computedStyle = getComputedStyle(this.section);
    const columns = parseInt(computedStyle.getPropertyValue('--review-main-columns') || 2, 10);
    
    // Calculate gap from CSS clamp
    const gapValue = clamp(0.625, 0.3169, 1.3146, 1.5, gridWidth);
    const gap = gapValue * 16; // Convert rem to px
    
    const columnWidth = (gridWidth - (columns - 1) * gap) / columns;
    
    // Track column heights
    const columnHeights = new Array(columns).fill(0);
    
    // Get visible items (not hidden by filter or pagination)
    const visibleItems = Array.from(this.items).filter(item => 
      !item.hidden && !item.classList.contains('review-main__item--paginated-hidden')
    );
    
    visibleItems.forEach(item => {
      // Check if item should span full width on mobile
      const isFullWidth = item.classList.contains('review-main__item--full-width-mobile') && window.innerWidth < 768;
      
      if (isFullWidth) {
        // Full width item: place below all columns
        const maxHeight = Math.max(...columnHeights);
        item.style.left = '0';
        item.style.top = `${maxHeight}px`;
        item.style.width = '100%';
        
        // Update all column heights
        const itemHeight = item.offsetHeight + gap;
        for (let i = 0; i < columns; i++) {
          columnHeights[i] = maxHeight + itemHeight;
        }
      } else {
        // Find shortest column
        const shortestColumn = columnHeights.indexOf(Math.min(...columnHeights));
        
        // Position item
        const left = shortestColumn * (columnWidth + gap);
        const top = columnHeights[shortestColumn];
        
        item.style.left = `${left}px`;
        item.style.top = `${top}px`;
        item.style.width = `${columnWidth}px`;
        
        // Update column height
        columnHeights[shortestColumn] += item.offsetHeight + gap;
      }
    });
    
    // Set grid height
    this.grid.style.height = `${Math.max(...columnHeights)}px`;
  }
  
  /**
   * Initialize Splide slider for filter pills on mobile
   */
  initFilterSlider() {
    if (!this.filterSlider || typeof Splide === 'undefined') {
      return;
    }
    
    this.filterSplide = new Splide(this.filterSlider, {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      autoWidth: true,
      focus: 0,
      omitEnd: true,
      pagination: false,
      arrows: false,
      drag: true,
      gap: '8px',
      mediaQuery: 'min',
      breakpoints: {
        768: {
          // Destroy slider on tablet/desktop
          destroy: true,
        }
      }
    });
    
    this.filterSplide.mount();
    
    // Remove tabindex from slides to prevent focus issues
    this.filterSplide.on('mounted', () => {
      const slides = this.filterSlider.querySelectorAll('.splide__slide');
      slides.forEach(slide => {
        slide.removeAttribute('tabindex');
      });
    });
  }
  
  /**
   * Filter pill click handler
   */
  handleFilterClick(event) {
    const pill = event.currentTarget;
    const filter = pill.dataset.filter;
    
    if (filter === this.activeFilter) return;
    
    // Update active state
    this.filterPills.forEach(p => {
      p.classList.remove('review-main__filter-pill--active');
      p.setAttribute('aria-selected', 'false');
    });
    pill.classList.add('review-main__filter-pill--active');
    pill.setAttribute('aria-selected', 'true');
    
    // Scroll slider to active pill on mobile
    if (this.filterSplide && !this.filterSplide.state.is(Splide.STATES.DESTROYED)) {
      const slideIndex = Array.from(this.filterPills).indexOf(pill);
      if (slideIndex >= 0) {
        this.filterSplide.go(slideIndex);
      }
    }
    
    this.activeFilter = filter;
    this.currentPage = 1; // Reset pagination when filter changes
    this.filterItems(filter);
  }
  
  /**
   * Filter items by tag with fade animation
   */
  filterItems(filter) {
    const hideDelay = 200; // Match CSS transition duration
    const showDelay = 300; // Match CSS transition duration
    const staggerDelay = 40; // Delay between each item
    
    // Step 1: Fade out currently visible items
    const currentlyVisible = Array.from(this.items).filter(item => 
      !item.hidden && !item.classList.contains('review-main__item--paginated-hidden')
    );
    
    currentlyVisible.forEach(item => {
      item.classList.add('review-main__item--hiding');
    });
    
    // Step 2: After fade out, update visibility and fade in new items
    setTimeout(() => {
      // Clean up all animation classes and hide all
      this.items.forEach(item => {
        item.classList.remove('review-main__item--hiding', 'review-main__item--animate-ready', 'review-main__item--showing');
        item.hidden = true;
      });
      
      // Determine which items to show based on filter
      const itemsToShow = [];
      
      if (filter === 'all') {
        itemsToShow.push(...this.items);
      } else {
        this.items.forEach(item => {
          const itemTags = item.dataset.filterTags || '';
          const tagsArray = itemTags.split(',').map(t => t.trim());
          if (tagsArray.includes(filter)) {
            itemsToShow.push(item);
          }
        });
      }
      
      // Show matching items with animate-ready class (invisible but in DOM)
      itemsToShow.forEach(item => {
        item.hidden = false;
        item.classList.add('review-main__item--animate-ready');
      });
      
      // Apply pagination (this will hide items beyond current page)
      this.applyPagination();
      
      // Update show more button visibility
      this.updatePagination();
      
      // Get items that should animate (visible and not paginated-hidden)
      const newlyVisible = this.getVisibleItems().filter(item => 
        !item.classList.contains('review-main__item--paginated-hidden')
      );
      
      // Force reflow to ensure animate-ready styles are applied
      if (newlyVisible.length > 0) {
        newlyVisible[0].offsetHeight;
      }
      
      // Animate in with stagger
      newlyVisible.forEach((item, index) => {
        setTimeout(() => {
          item.classList.add('review-main__item--showing');
        }, index * staggerDelay);
      });
      
      // Clean up animation classes after all animations complete
      const totalAnimationTime = showDelay + (newlyVisible.length * staggerDelay);
      setTimeout(() => {
        newlyVisible.forEach(item => {
          item.classList.remove('review-main__item--animate-ready', 'review-main__item--showing');
        });
        // Re-layout masonry after animation
        this.layoutMasonry();
      }, totalAnimationTime);
      
      // Layout masonry immediately for positioning
      this.layoutMasonry();
      
    }, hideDelay);
  }
  
  /**
   * Get visible (non-filtered) items
   */
  getVisibleItems() {
    return Array.from(this.items).filter(item => !item.hidden);
  }
  
  /**
   * Handle show more button click
   */
  handleShowMore() {
    this.currentPage++;
    this.applyPagination(true); // Animate newly revealed items
    this.updatePagination();
  }
  
  /**
   * Apply pagination to visible items
   * @param {boolean} animate - Whether to animate newly revealed items
   */
  applyPagination(animate = false) {
    const visibleItems = this.getVisibleItems();
    const maxVisible = this.currentPage * this.itemsPerPage;
    const previousMaxVisible = (this.currentPage - 1) * this.itemsPerPage;
    const showDelay = 300; // Match CSS transition duration
    const staggerDelay = 40; // Delay between each item
    const newlyRevealed = [];
    
    visibleItems.forEach((item, index) => {
      if (index < maxVisible) {
        const wasHidden = item.classList.contains('review-main__item--paginated-hidden');
        item.classList.remove('review-main__item--paginated-hidden');
        
        // Track newly revealed items for animation
        if (animate && wasHidden && index >= previousMaxVisible) {
          newlyRevealed.push(item);
        }
      } else {
        item.classList.add('review-main__item--paginated-hidden');
      }
    });
    
    // Animate newly revealed items with stagger
    if (animate && newlyRevealed.length > 0) {
      // Set initial state (invisible)
      newlyRevealed.forEach(item => {
        item.classList.add('review-main__item--animate-ready');
      });
      
      // Force reflow
      newlyRevealed[0].offsetHeight;
      
      // Animate in with stagger
      newlyRevealed.forEach((item, index) => {
        setTimeout(() => {
          item.classList.add('review-main__item--showing');
        }, index * staggerDelay);
      });
      
      // Clean up animation classes after all animations complete
      const totalAnimationTime = showDelay + (newlyRevealed.length * staggerDelay);
      setTimeout(() => {
        newlyRevealed.forEach(item => {
          item.classList.remove('review-main__item--animate-ready', 'review-main__item--showing');
        });
      }, totalAnimationTime);
      
      // Re-layout masonry
      this.layoutMasonry();
    }
    
    // Always re-layout when pagination changes
    this.layoutMasonry();
  }
  
  /**
   * Update show more button visibility
   */
  updatePagination() {
    if (!this.showMoreWrapper) return;
    
    const visibleItems = this.getVisibleItems();
    const maxVisible = this.currentPage * this.itemsPerPage;
    const hasMoreItems = visibleItems.length > maxVisible;
    
    this.showMoreWrapper.hidden = !hasMoreItems;
  }
  
  /**
   * Cleanup
   */
  destroy() {
    // Destroy Splide
    if (this.filterSplide) {
      this.filterSplide.destroy();
      this.filterSplide = null;
    }
    
    // Destroy resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    // Cancel pending layout
    if (this.layoutTimeout) {
      cancelAnimationFrame(this.layoutTimeout);
    }
    
    // Remove event listeners
    this.filterPills.forEach(pill => {
      pill.removeEventListener('click', this.handleFilterClick);
    });
    
    if (this.showMoreBtn) {
      this.showMoreBtn.removeEventListener('click', this.handleShowMore);
    }
  }
}

/**
 * Helper: Calculate CSS clamp value
 * clamp(min, preferred-slope, preferred-vw-coefficient, max, containerWidth)
 */
function clamp(minRem, slope, vwCoeff, maxRem, containerWidth) {
  const vw = containerWidth / 100;
  const preferred = slope + (vwCoeff * vw / 16);
  return Math.max(minRem, Math.min(preferred, maxRem));
}

/**
 * Initialize sections
 */
function initReviewMain() {
  document.querySelectorAll('[data-section-type="review-main"]').forEach(section => {
    if (!section.reviewMainInstance) {
      section.reviewMainInstance = new ReviewMainSection(section);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReviewMain);
} else {
  initReviewMain();
}

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('[data-section-type="review-main"]');
    if (section) {
      if (section.reviewMainInstance) {
        section.reviewMainInstance.destroy();
      }
      section.reviewMainInstance = new ReviewMainSection(section);
    }
  });
  
  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('[data-section-type="review-main"]');
    if (section?.reviewMainInstance) {
      section.reviewMainInstance.destroy();
    }
  });
}
